import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import { DashboardData, DashboardFilters, DashboardTab } from '../types/dashboard.types';
import { BRAND_COLORS } from '../../../styles/theme';
import { PersonnelMember } from '../../../types/api';
import { useTranslation } from '../../../i18n';
import { usePersonnelQuery } from './overview.queries';
import { LatestFlightsSection } from '../components/LatestFlightsSection';
import { DroneCardsSection } from './DroneCardsSection';
import { PersonnelGrid } from './PersonnelGrid';

interface OverviewTabProps {
  data: DashboardData;
  onFilterChange: (filters: Partial<DashboardFilters>) => void;
  onTabChange: (tab: DashboardTab) => void;
}

export const OverviewTab: React.FC<OverviewTabProps> = ({ data, onFilterChange, onTabChange }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Personnel data from TanStack Query (STD-015)
  const personnelQuery = usePersonnelQuery();
  const pilots: PersonnelMember[] = personnelQuery.data?.pilots ?? [];
  const maintenanceEngineers: PersonnelMember[] = personnelQuery.data?.maintenanceEngineers ?? [];
  const personnelLoading = personnelQuery.isLoading;
  const personnelError = personnelQuery.isError ? 'Failed to load personnel data' : null;

  // Use data from props instead of making separate API calls
  const { serialNumbers, allDronesSummary } = data;

  const handlePersonClick = (personId: string) => {
    navigate(`/user/${personId}`);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ color: BRAND_COLORS.forestGreen.main }}>
        {t('overview.fleetOverview')}
      </Typography>

      {/* Fleet summary + drone grid */}
      <DroneCardsSection
        serialNumbers={serialNumbers}
        dronesSummary={allDronesSummary}
        onFilterChange={onFilterChange}
        onTabChange={onTabChange}
      />

      {/* Latest Flights Section */}
      <LatestFlightsSection />

      {/* Personnel Overview Section */}
      <Box sx={{ mt: 6 }}>
        <Typography variant="h4" gutterBottom sx={{ color: BRAND_COLORS.forestGreen.main }}>
          {t('overview.personnel.title')}
        </Typography>

        {personnelLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {personnelError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {personnelError}
          </Alert>
        )}

        {!personnelLoading && !personnelError && (
          <>
            <PersonnelGrid
              role="pilot"
              people={pilots}
              headingKey="overview.personnel.pilots"
              emptyKey="overview.personnel.noPilots"
              onPersonClick={handlePersonClick}
            />
            <PersonnelGrid
              role="maintenance"
              people={maintenanceEngineers}
              headingKey="overview.personnel.maintenanceEngineers"
              emptyKey="overview.personnel.noMaintenanceEngineers"
              onPersonClick={handlePersonClick}
            />
          </>
        )}
      </Box>
    </Box>
  );
};
